import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectIsAdmin } from '../../features/auth/authSlice';
import { LoadingButton } from '../ui/LoadingSpinner';

function UserForm({ user, onSubmit, isLoading, onCancel }) {
  const isAdmin = useSelector(selectIsAdmin);
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    roles: [],
    is_active: true
  });

  const [errors, setErrors] = useState({});

  // Available roles from backend schema
  const availableRoles = [
    'HR',
    'Broker', 
    'CRM',
    'Claims_Officer',
    'Approver',
    'Finance',
    'Admin'
  ];

  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username || '',
        email: user.email || '',
        password: '',
        confirmPassword: '',
        roles: user.userRoles?.map(ur => ur.role?.name) || [],
        is_active: user.is_active !== undefined ? user.is_active : true
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear field-specific errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Password confirmation validation
    if (name === 'password' && formData.confirmPassword) {
      setTimeout(() => {
        if (formData.confirmPassword !== value) {
          setErrors(prev => ({ ...prev, confirmPassword: 'Passwords do not match' }));
        }
      }, 300);
    }

    if (name === 'confirmPassword') {
      setTimeout(() => {
        if (formData.password !== value) {
          setErrors(prev => ({ ...prev, confirmPassword: 'Passwords do not match' }));
        } else {
          setErrors(prev => ({ ...prev, confirmPassword: '' }));
        }
      }, 300);
    }
  };
  
  // Handle role checkbox changes
  const handleRoleChange = (role) => {
    setFormData(prev => {
      const currentRoles = [...prev.roles];
      
      if (currentRoles.includes(role)) {
        // Remove role if already selected
        return {
          ...prev,
          roles: currentRoles.filter(r => r !== role)
        };
      } else {
        // Add role if not selected
        return {
          ...prev,
          roles: [...currentRoles, role]
        };
      }
    });

    // Clear roles error if at least one role is selected
    if (errors.roles) {
      setErrors(prev => ({ ...prev, roles: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Username validation
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    // Password validation (required for new users, optional for updates)
    if (!user && !formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password && formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    // Confirm password validation
    if (formData.password && !formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Roles validation
    if (formData.roles.length === 0) {
      newErrors.roles = 'At least one role must be selected';
    }

    // Admin role validation - only admins can assign admin role
    if (formData.roles.includes('Admin') && !isAdmin) {
      newErrors.roles = 'You do not have permission to assign Admin role';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Prepare submission data
    const submitData = {
      username: formData.username.trim(),
      email: formData.email.trim(),
      roles: formData.roles,
      is_active: formData.is_active
    };

    // Only include password if it's provided
    if (formData.password) {
      submitData.password = formData.password;
    }

    onSubmit(submitData);
  };

  // Only admins can access this form
  if (!isAdmin) {
    return (
      <div className="min-h-96 flex items-center justify-center bg-silver-mist">
        <div className="card max-w-md w-full mx-4">
          <div className="card-header text-center">
            <h2 className="text-xl font-display">Access Denied</h2>
          </div>
          <div className="card-body text-center">
            <div className="text-error text-base mb-4">
              You do not have permission to manage users.
            </div>
            <p className="text-steel text-sm">
              Contact your administrator for access to user management features.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-silver-mist py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header Section */}
        <div className="mb-8">
          <div className="card">
            <div className="card-header">
              <h1 className="text-2xl font-display tracking-tight">
                {user ? 'Edit User Account' : 'Create New User Account'}
              </h1>
              <p className="text-crystal-white/85 text-sm mt-1 font-normal">
                {user ? 'Update user information and permissions' : 'Configure user credentials and role assignments'}
              </p>
            </div>
          </div>
        </div>

        {/* Main Form */}
        <div className="card">
          <div className="card-body">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Account Information Section */}
              <div className="space-y-6">
                <div className="border-b border-silver-mist pb-4">
                  <h3 className="text-lg font-display text-deep-jungle mb-2">Account Information</h3>
                  <p className="text-steel text-sm">Basic user account details and credentials</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Username Field */}
                  <div className="space-y-2">
                    <label htmlFor="username" className="block text-sm font-medium text-graphite">
                      Username
                      <span className="text-error ml-1">*</span>
                    </label>
                    <input
                      type="text"
                      id="username"
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border-2 bg-crystal-white text-graphite placeholder-steel/60 transition-all duration-200 focus:outline-none ${
                        errors.username 
                          ? 'border-error focus:border-error focus:ring-4 focus:ring-error/10' 
                          : 'border-woodland-moss/20 focus:border-fresh-leaf focus:ring-4 focus:ring-fresh-leaf/10'
                      }`}
                      placeholder="Enter username"
                    />
                    {errors.username && (
                      <div className="flex items-start space-x-2 mt-2">
                        <div className="flex-shrink-0 w-1 h-4 bg-error mt-0.5"></div>
                        <p className="text-sm text-error">{errors.username}</p>
                      </div>
                    )}
                  </div>

                  {/* Email Field */}
                  <div className="space-y-2">
                    <label htmlFor="email" className="block text-sm font-medium text-graphite">
                      Email Address
                      <span className="text-error ml-1">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border-2 bg-crystal-white text-graphite placeholder-steel/60 transition-all duration-200 focus:outline-none ${
                        errors.email 
                          ? 'border-error focus:border-error focus:ring-4 focus:ring-error/10' 
                          : 'border-woodland-moss/20 focus:border-fresh-leaf focus:ring-4 focus:ring-fresh-leaf/10'
                      }`}
                      placeholder="Enter email address"
                    />
                    {errors.email && (
                      <div className="flex items-start space-x-2 mt-2">
                        <div className="flex-shrink-0 w-1 h-4 bg-error mt-0.5"></div>
                        <p className="text-sm text-error">{errors.email}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Password Field */}
                  <div className="space-y-2">
                    <label htmlFor="password" className="block text-sm font-medium text-graphite">
                      Password
                      {!user && <span className="text-error ml-1">*</span>}
                      {user && (
                        <span className="text-steel text-xs ml-2 font-normal">(leave blank to keep current)</span>
                      )}
                    </label>
                    <input
                      type="password"
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border-2 bg-crystal-white text-graphite placeholder-steel/60 transition-all duration-200 focus:outline-none ${
                        errors.password 
                          ? 'border-error focus:border-error focus:ring-4 focus:ring-error/10' 
                          : 'border-woodland-moss/20 focus:border-fresh-leaf focus:ring-4 focus:ring-fresh-leaf/10'
                      }`}
                      placeholder="Enter password"
                    />
                    {errors.password && (
                      <div className="flex items-start space-x-2 mt-2">
                        <div className="flex-shrink-0 w-1 h-4 bg-error mt-0.5"></div>
                        <p className="text-sm text-error">{errors.password}</p>
                      </div>
                    )}
                  </div>

                  {/* Confirm Password Field */}
                  {formData.password && (
                    <div className="space-y-2">
                      <label htmlFor="confirmPassword" className="block text-sm font-medium text-graphite">
                        Confirm Password
                        <span className="text-error ml-1">*</span>
                      </label>
                      <input
                        type="password"
                        id="confirmPassword"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border-2 bg-crystal-white text-graphite placeholder-steel/60 transition-all duration-200 focus:outline-none ${
                          errors.confirmPassword 
                            ? 'border-error focus:border-error focus:ring-4 focus:ring-error/10' 
                            : 'border-woodland-moss/20 focus:border-fresh-leaf focus:ring-4 focus:ring-fresh-leaf/10'
                        }`}
                        placeholder="Confirm password"
                      />
                      {errors.confirmPassword && (
                        <div className="flex items-start space-x-2 mt-2">
                          <div className="flex-shrink-0 w-1 h-4 bg-error mt-0.5"></div>
                          <p className="text-sm text-error">{errors.confirmPassword}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Permissions Section */}
              <div className="space-y-6">
                <div className="border-b border-silver-mist pb-4">
                  <h3 className="text-lg font-display text-deep-jungle mb-2">Permissions & Access</h3>
                  <p className="text-steel text-sm">Configure user roles and account status</p>
                </div>

                {/* Roles Selection */}
                <div className="space-y-4">
                  <label className="block text-sm font-medium text-graphite">
                    User Roles
                    <span className="text-error ml-1">*</span>
                    <span className="text-steel text-xs ml-2 font-normal">(select at least one)</span>
                  </label>
                  
                  <div className="bg-silver-mist/50 p-6 border-l-4 border-woodland-moss">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {availableRoles.map(role => (
                        <label 
                          key={role} 
                          className="flex items-center space-x-3 p-3 bg-crystal-white border-2 border-woodland-moss/20 cursor-pointer transition-all duration-200 hover:border-fresh-leaf hover:bg-fresh-leaf/5"
                        >
                          <input
                            type="checkbox"
                            checked={formData.roles.includes(role)}
                            onChange={() => handleRoleChange(role)}
                            className="w-5 h-5 text-fresh-leaf border-2 border-woodland-moss/30 focus:ring-fresh-leaf focus:ring-2 focus:ring-offset-1"
                          />
                          <span className="text-sm font-medium text-graphite">{role.replace('_', ' ')}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  {errors.roles && (
                    <div className="flex items-start space-x-2 mt-2">
                      <div className="flex-shrink-0 w-1 h-4 bg-error mt-0.5"></div>
                      <p className="text-sm text-error">{errors.roles}</p>
                    </div>
                  )}
                </div>

                {/* Account Status */}
                <div className="space-y-4">
                  <label className="block text-sm font-medium text-graphite">Account Status</label>
                  <div className="bg-silver-mist/50 p-4 border-l-4 border-zesty-lime">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={formData.is_active}
                        onChange={handleChange}
                        className="w-5 h-5 text-zesty-lime border-2 border-woodland-moss/30 focus:ring-zesty-lime focus:ring-2 focus:ring-offset-1"
                      />
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-graphite">Active Account</span>
                        <span className="text-xs text-steel">User can log in and access assigned features</span>
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="border-t border-silver-mist pt-8">
                <div className="flex flex-col sm:flex-row sm:justify-end space-y-3 sm:space-y-0 sm:space-x-4">
                  <button
                    type="button"
                    onClick={onCancel}
                    className="btn-secondary w-full sm:w-auto px-8 py-3 font-medium transition-all duration-200 disabled:opacity-50"
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <LoadingButton
                    type="submit"
                    isLoading={isLoading}
                    loadingText={user ? "Updating User..." : "Creating User..."}
                    variant="primary"
                    className="btn-primary w-full sm:w-auto px-8 py-3 font-medium transition-all duration-200 disabled:opacity-75"
                  >
                    {user ? 'Update User' : 'Create User'}
                  </LoadingButton>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserForm;
